import { useEffect, useState } from 'react'

interface TermlyConsentState {
  analytics?: boolean
  performance?: boolean
  advertising?: boolean
  social_networking?: boolean
}

interface TermlyAPI {
  on: (event: string, callback: (data: any) => void) => void
  getConsentState: () => TermlyConsentState
}

declare global {
  interface Window {
    Termly?: TermlyAPI
    __termlyConsentListenerRegistered?: boolean
  }
}

// Custom event name for broadcasting consent changes to all components
const CONSENT_CHANGE_EVENT = 'termly-consent-changed'

/**
 * Shared hook for Termly consent state.
 * Uses a custom event to broadcast consent changes to all component instances.
 */
export function useTermlyConsent(debug: boolean = false) {
  const [hasConsent, setHasConsent] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    // Function to check consent state
    const checkConsentState = () => {
      if (!window.Termly) {
        return false
      }

      try {
        const consentState = window.Termly.getConsentState()
        return consentState?.analytics === true
      } catch (error) {
        if (debug) {
          console.warn('Error checking Termly consent state:', error)
        }
        return false
      }
    }

    // Function to broadcast consent change to all components
    const broadcastConsentChange = (hasConsent: boolean) => {
      const event = new CustomEvent(CONSENT_CHANGE_EVENT, { detail: { hasConsent } })
      window.dispatchEvent(event)
    }

    // Function to register the global Termly listener (only once)
    const registerListener = () => {
      if (!window.Termly || window.__termlyConsentListenerRegistered) {
        return false
      }

      window.__termlyConsentListenerRegistered = true

      const handleConsentChange = (data: { consentState?: TermlyConsentState }) => {
        const analyticsConsent = data.consentState?.analytics === true

        if (debug) {
          console.log('Termly consent changed:', {
            analytics: analyticsConsent,
            fullState: data.consentState
          })
        }

        // Broadcast to ALL components using this hook
        broadcastConsentChange(analyticsConsent)
      }

      window.Termly.on('consent', handleConsentChange)

      if (debug) {
        console.log('[useTermlyConsent] Termly listener registered')
      }

      return true
    }

    // Listen for consent change events from other components
    const handleCustomConsentEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ hasConsent: boolean }>
      setHasConsent(customEvent.detail.hasConsent)
    }

    window.addEventListener(CONSENT_CHANGE_EVENT, handleCustomConsentEvent)

    // Check initial consent state
    const initialConsent = checkConsentState()
    if (initialConsent) {
      broadcastConsentChange(true)
    }

    // Try to register listener immediately
    const registeredImmediately = registerListener()

    // If Termly isn't loaded yet, poll for it
    const pollInterval = registeredImmediately
      ? null
      : setInterval(() => {
          if (registerListener()) {
            clearInterval(pollInterval!)
          }
        }, 100) // Check every 100ms

    // Cleanup: clear interval after 10 seconds (Termly should load within this time)
    const timeout = registeredImmediately
      ? null
      : setTimeout(() => {
          if (pollInterval) clearInterval(pollInterval)
          if (debug && !window.Termly) {
            console.warn('[useTermlyConsent] Termly did not load within 10 seconds')
          }
        }, 10000)

    return () => {
      if (pollInterval) clearInterval(pollInterval)
      if (timeout) clearTimeout(timeout)
      window.removeEventListener(CONSENT_CHANGE_EVENT, handleCustomConsentEvent)
    }
  }, [debug])

  return hasConsent
}
